<template>
    <Card class="font-card">
        <div class="font-card__thumbnail" :style="thumbnailStyle">
            <p v-if="!thumbnailAvailable">Font preview unavailable</p>
        </div>
        <template #title>
            <Tooltip v-if="!font.postScriptName" class="font-card__postscript-warning">
                <Icon type="md-warning" />
                <template #content>
                    This font has no PostScript name
                    <br />
                    so will not work for PSD templates
                </template>
            </Tooltip>
            {{ font.name }}
        </template>
        <template #actionItems>
            <Dropdown transfer trigger="click">
                <CardFooterButton>
                    <Icon type="ios-more" class="font-card-option--dots" />
                </CardFooterButton>
                <template #list>
                    <DropdownMenu>
                        <DropdownItem v-if="userCanManageFonts" @click="toggleEditFontModalIsVisible">
                            Edit
                        </DropdownItem>
                        <DropdownItem v-if="userCanManageFonts" @click="toggleDeleteFontModalIsVisible">
                            Delete
                        </DropdownItem>
                        <DropdownItem @click="download">Download</DropdownItem>
                    </DropdownMenu>
                </template>
            </Dropdown>
        </template>
        <EditFontModal
            v-if="editFontModalIsVisible"
            :font="font"
            :upload-config="uploadConfig"
            @close="toggleEditFontModalIsVisible"
            @fontUpdated="edit"
        />
        <DeleteFontModal
            v-if="deleteFontModalIsVisible"
            :font-id="font._id"
            :name="font.name"
            @close="toggleDeleteFontModalIsVisible"
            @fontDeleted="deleted"
        />
    </Card>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import type { Font } from "hoxton-types";

import Card from "@/components/Card/Card.vue";
import CardFooterButton from "@/components/Card/CardFooterButton.vue";
import EditFontModal from "@/components/EditFontModal.vue";
import DeleteFontModal from "@/components/DeleteFontModal.vue";
import downloadFile from "@/utils/downloadFile";
import * as auth from "@/services/Auth";
import type { MastheadUploadConfig } from "@/types/types";

type Props = {
    font: Font;
    clientId: string;
    uploadConfig: MastheadUploadConfig;
};

const { font, clientId, uploadConfig } = defineProps<Props>();

const emit = defineEmits(["edit", "deleted"]);

const editFontModalIsVisible = ref(false);
const deleteFontModalIsVisible = ref(false);

const thumbnailAvailable = computed(() => font.thumbnail != null);

const thumbnailStyle = computed(() => {
    if (!thumbnailAvailable.value) {
        return {};
    }
    return {
        backgroundImage: `url(${font.thumbnail})`
    };
});

const userCanManageFonts = computed(() =>
    auth.userCan(auth.Actions.CanManageFonts, {
        clientId: clientId
    })
);

function edit() {
    emit("edit");
}

function deleted() {
    emit("deleted");
}

function download() {
    const url = new URL(font.url, window.location.origin);
    const extension = url.pathname.split(".").pop();
    downloadFile(font.url, `${font.postScriptName}.${extension}`);
}

function toggleEditFontModalIsVisible() {
    editFontModalIsVisible.value = !editFontModalIsVisible.value;
}

function toggleDeleteFontModalIsVisible() {
    deleteFontModalIsVisible.value = !deleteFontModalIsVisible.value;
}
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.font-card {
    .font-card__thumbnail {
        align-items: center;
        background: no-repeat center;
        background-size: cover;
        color: $grey5;
        display: flex;
        height: 200px;
        justify-content: center;
        width: 100%;
    }

    .font-card__postscript-warning {
        color: $warning-color;
        margin-right: $spacing-small;
    }

    .card__footer-title {
        line-height: 24px;
    }
}
</style>
